import AppTheme from "@toothfairy/shared-ui/AppTheme";
import React, { useEffect, useState } from "react";
import { View } from "react-native";
import ToothFairyButton from "../../../Components/ToothFairyComponents/ToothFairyButton";
import ToothFairySlider from "../../../Components/ToothFairyComponents/ToothFairySlider";
import Constants from "../../../Constants";
import AppDropDown from "@toothfairy/shared-ui/AppDropDown";
import AppMultipleSelectInput from "@toothfairy/shared-ui/AppMultipleSelectInput";
import AppInputLabel from "@toothfairy/shared-ui/AppInputLabel";
import useWorkspaces from "../../../Hooks/useWorkspaces";

const availableLayers = [
  { id: "k_proj", type: "all" },
  { id: "w2", type: "mistral" },
  { id: "w1", type: "mistral" },
  { id: "gate", type: "mistral" },
  { id: "w3", type: "mistral" },
  { id: "o_proj", type: "all" },
  { id: "q_proj", type: "all" },
  { id: "v_proj", type: "all" },
  { id: "up_proj", type: "all" },
  { id: "down_proj", type: "other" },
  { id: "gate_proj", type: "other" },
];

const FineTuningConfigSelection = ({
  trainingConfig,
  setTrainingConfig,
  trainingLogId,
  handleFineTuningStart = () => {},
}) => {
  const { getTrainingLogInfo } = useWorkspaces();
  const { currentTheme } = AppTheme.useTheme();
  const [baseModel, setBaseModel] = useState(null);
  const colourStyles = {
    control: (styles) => ({
      ...styles,
      backgroundColor: currentTheme?.theme?.inputBackgroundColor,
      borderColor: currentTheme?.theme?.border_color,
      fontFamily: "normalFont",
      fontSize: currentTheme?.theme?.small_font_size,
      borderRadius: 10,
      color: currentTheme?.theme?.font_color,
    }),
    input: (base) => ({
      ...base,
      color: currentTheme?.theme?.font_color, // This would change the color of the text inside the input
    }),
    option: (styles, { data }) => {
      let color = currentTheme?.theme?.font_color;
      return {
        ...styles,
        backgroundColor: currentTheme?.theme?.transparent,
        color: currentTheme?.theme?.font_color,
        cursor: "pointer",

        ":active": {
          ...styles[":active"],
          backgroundColor: currentTheme?.theme?.primary,
        },
        ":hover": {
          ...styles[":hover"],
          backgroundColor: currentTheme?.theme?.primary,
        },
      };
    },
    singleValue: (styles, { data }) => ({
      ...styles,
      color: currentTheme?.theme?.font_color,
    }),
    multiValue: (styles, { data }) => {
      let color = currentTheme?.theme?.font_color;
      return {
        ...styles,
        backgroundColor: currentTheme?.theme?.primary,
      };
    },
    multiValueLabel: (styles, { data }) => ({
      ...styles,
      color: currentTheme?.theme?.font_color,
    }),
    multiValueRemove: (styles, { data }) => ({
      ...styles,
      color: currentTheme?.theme?.font_color,
      ":hover": {
        backgroundColor: currentTheme?.theme?.primary,
        color: "white",
      },
    }),
    menu: (styles) => ({
      ...styles,
      backgroundColor: currentTheme?.theme?.inputBackgroundColor,
      borderColor: currentTheme?.theme?.lightGray,
      fontFamily: "normalFont",
      fontSize: currentTheme?.theme?.small_font_size,
    }),
  };
  useEffect(() => {
    if (trainingLogId) {
      setBaseModel(
        getTrainingLogInfo(trainingLogId)?.trainingConfig?.baseModel
      );
    }
  }, [trainingLogId]);
  return (
    <View
      style={{
        marginLeft: 20,
        maxWidth: 450,
        paddingLeft: 5,
      }}
    >
      {getTrainingLogInfo(trainingLogId)?.type == "conversationalTraining" ? (
        <>
          {" "}
          <ToothFairySlider
            step={0.000001}
            maximumValue={0.01}
            minimumValue={0.000001}
            label="Learning rate (%)"
            onSubmit={(v) =>
              setTrainingConfig({
                ...trainingConfig,
                learningRateRC: v,
              })
            }
            initialValue={
              trainingConfig?.learningRateRC || Constants.learningRateRC
            }
          />
          <ToothFairySlider
            step={0.01}
            maximumValue={1}
            minimumValue={0.01}
            label="Learning rate peak ratio"
            onSubmit={(v) =>
              setTrainingConfig({
                ...trainingConfig,
                peakLearningRateRC: v,
              })
            }
            initialValue={
              trainingConfig?.peakLearningRateRC || Constants.peakLearningRate
            }
          />
          <ToothFairySlider
            step={0.01}
            maximumValue={1}
            minimumValue={0.01}
            label="Learning rate warmup ratio"
            onSubmit={(v) =>
              setTrainingConfig({
                ...trainingConfig,
                warmUpLearningRateRC: v,
              })
            }
            initialValue={
              trainingConfig?.warmUpLearningRateRC ||
              Constants.warmUpLearningRate
            }
          />
          <ToothFairySlider
            step={1}
            maximumValue={5000}
            minimumValue={1}
            label="Min dataset for training test"
            onSubmit={(v) =>
              setTrainingConfig({
                ...trainingConfig,
                datasetTestSizeThresholdRC: v,
              })
            }
            initialValue={
              trainingConfig?.datasetTestSizeThresholdRC ||
              Constants.datasetTestSizeThresholdRC
            }
          />
          <ToothFairySlider
            step={1}
            maximumValue={35}
            minimumValue={1}
            label="Test dataset size (%)"
            onSubmit={(v) =>
              setTrainingConfig({
                ...trainingConfig,
                testSizeRC: v,
              })
            }
            initialValue={
              trainingConfig?.testSizeRC || Constants.testSizeRC * 100
            }
          />
          <ToothFairySlider
            step={1}
            maximumValue={100}
            minimumValue={1}
            label="Number of evaluations"
            onSubmit={(v) =>
              setTrainingConfig({
                ...trainingConfig,
                evaluationNumberRC: v,
              })
            }
            initialValue={
              trainingConfig?.evaluationNumberRC || Constants.evaluationNumber
            }
          />
          <ToothFairySlider
            maximumValue={20}
            minimumValue={1}
            label="Max epochs"
            onSubmit={(v) =>
              setTrainingConfig({
                ...trainingConfig,
                trainingEpochsRC: v,
              })
            }
            initialValue={
              trainingConfig?.trainingEpochsRC || Constants.baseTrainingRCEpochs
            }
          />
          <ToothFairySlider
            step={1}
            maximumValue={20}
            minimumValue={1}
            label="Number of checkpoints"
            onSubmit={(v) =>
              setTrainingConfig({
                ...trainingConfig,
                checkpointsNumberRC: v,
              })
            }
            initialValue={
              trainingConfig?.checkpointsNumberRC || Constants.checkpointsNumber
            }
          />
          <ToothFairySlider
            step={1}
            maximumValue={
              Constants.baseModels?.find((u) => u?.id == baseModel)
                ?.maxBatchSize
            }
            minimumValue={8}
            label="Batch size"
            onSubmit={(v) =>
              setTrainingConfig({
                ...trainingConfig,
                batchSizeRC: v,
              })
            }
            initialValue={8}
          />
          <ToothFairySlider
            step={1}
            maximumValue={64}
            minimumValue={1}
            label="Rank for LoRA adapter weights"
            onSubmit={(v) =>
              setTrainingConfig({
                ...trainingConfig,
                loraRRC: v,
              })
            }
            initialValue={trainingConfig?.loraRRC || Constants.loraR}
          />
          <ToothFairySlider
            step={1}
            maximumValue={16}
            minimumValue={1}
            label="Alpha value for LoRA adapter"
            onSubmit={(v) =>
              setTrainingConfig({
                ...trainingConfig,
                loraAlphaRC: v,
              })
            }
            initialValue={trainingConfig?.loraAlphaRC || Constants.loraAlpha}
          />
          <ToothFairySlider
            step={0.1}
            maximumValue={1}
            minimumValue={0.1}
            label="Dropout for LoRA adapter"
            onSubmit={(v) =>
              setTrainingConfig({
                ...trainingConfig,
                loraDropoutRC: v,
              })
            }
            initialValue={
              trainingConfig?.loraDropoutRC || Constants.loraDropout
            }
          />
          <View
            style={{
              zIndex: 10,
              marginBottom: 10,
            }}
          >
            <AppInputLabel
              style={[{ marginLeft: 0, marginBottom: 10 }]}
              isMandatory={false}
              label={"Select LoRA layers"}
            />
            <AppMultipleSelectInput
              accessibilityLabel="ft_layers"
              onSelectionChange={(v) => {
                setTrainingConfig({
                  ...trainingConfig,
                  loraLayersRC: v,
                });
              }}
              labelOption={"label"}
              options={availableLayers
                ?.filter((u) => u?.type == "all" || u?.type == "other")
                ?.map((u) => {
                  return {
                    ...u,
                    value: u.id,
                    label: u?.id,
                  };
                })}
              groupByProperty="type"
              selectedItems={trainingConfig?.loraLayersRC || []}
              placeholder="Filter the layers to train"
              isAsync={false}
              uniqueKey="id"
              showDropDowns={true}
              readOnlyHeadings={true}
              styles={colourStyles}
            />
          </View>
          <AppDropDown
            showTextInputLabel={true}
            label={"Train on inputs"}
            inputLabelStyle={{
              marginLeft: 0,
            }}
            dropDownStyle={{
              minWidth: 100,
              borderColor: currentTheme?.theme?.border_color,
              maxWidth: 250,
            }}
            isElevated={false}
            dropdownIconColor="transparent"
            items={[
              { id: "auto", name: "Auto" },
              { id: true, name: "True" },
              { id: false, name: "False" },
            ]}
            propertyLabel="name"
            propertyValue="id"
            selectedValue={trainingConfig?.trainOnInputsRC}
            onValueChange={(itemValue, itemIndex) => {
              setTrainingConfig({
                ...trainingConfig,
                trainOnInputsRC: itemValue,
              });
            }}
          />
        </>
      ) : (
        <>
          <ToothFairySlider
            step={0.05}
            maximumValue={0.9}
            minimumValue={0.1}
            label="DPO beta"
            onSubmit={(v) =>
              setTrainingConfig({
                ...trainingConfig,
                dpoBetaRC: v,
              })
            }
            initialValue={trainingConfig?.dpoBetaGN || Constants.dpoBetaGN}
          />
        </>
      )}

      <ToothFairyButton
        wrapperStyle={{
          marginTop: 20,
          alignSelf: "center",
        }}
        title="Start"
        onPress={() => handleFineTuningStart(trainingLogId, trainingConfig)}
      />
    </View>
  );
};

export default FineTuningConfigSelection;
